import { useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Box,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
  TablePagination,
  Tooltip,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
} from "@mui/material";
import {
  Edit as EditIcon,
  Visibility as ViewIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  History as HistoryIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  FileDownload as FileDownloadIcon,
  VpnKey as PasswordIcon,
} from "@mui/icons-material";
import { useAuth } from "../../../../auth";
import ListLayout from "../../../../components/Layout/ListLayout";
import { UserFilters, type UserFiltersData } from "./components/UserFilters";
import { UserCards } from "./components/UserCards";
import type { SmartUserDto } from "../../../../api/bpm/model";
import { customInstanceBpm } from "../../../../api/axios-client";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { getListUserQueryOptions, useCreatePasswordLink, useEnableUser, useDisableUser, useDeleteUser } from "../../../../api/bpm/endpoints";
import { ConfirmModal } from "../../../../components/ConfirmModal";
import { LogsModal } from "../../../../components/LogsModal";
import { useTranslation } from "react-i18next";

const usersSearchSchema = z.object({
  page: z.number().catch(1),
  size: z.number().catch(10),
  q: z.string().optional(),
  cpf: z.string().optional(),
  matricula: z.string().optional(),
  stockingAreaId: z.string().optional(),
  jobId: z.string().optional(),
  accessProfileId: z.string().optional(),
});

type ExportStatusFilter = "all" | "active" | "inactive";

const appendQueryParam = (
  params: URLSearchParams,
  key: string,
  value?: string | number | boolean | Array<string | number | boolean>,
) => {
  if (value === undefined || value === "") return;

  if (Array.isArray(value)) {
    value.forEach((item) => params.append(key, String(item)));
    return;
  }

  params.append(key, String(value));
};

const downloadBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const getUsersFromExportResponse = (response: any): SmartUserDto[] => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.content)) return response.content;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.content)) return response.data.content;

  return [];
};

const escapeCsvValue = (value: unknown) => {
  if (value === undefined || value === null) return "";

  const stringValue = String(value);
  return /[;"\r\n]/.test(stringValue)
    ? `"${stringValue.replace(/"/g, '""')}"`
    : stringValue;
};

const buildUsersCsv = (users: SmartUserDto[], t: (key: string) => string) => {
  const headers = [
    "ID",
    t("common.name"),
    t("common.email"),
    t("admin.users.registrationNumber"),
    t("admin.users.sector"),
    t("admin.users.role"),
    t("admin.common.situation"),
  ];

  const rows = users.map((user) => [
    user.id,
    user.name,
    user.email,
    user.matriculaInstitucional,
    user.stockingAreas?.map((area) => area.name).filter(Boolean).join(", "),
    user.job?.name,
    user.enabled ? t("common.active") : t("common.inactive"),
  ]);

  return [headers, ...rows]
    .map((row) => row.map(escapeCsvValue).join(";"))
    .join("\r\n");
};

export const Route = createFileRoute("/__auth/admin/users/")({
  component: UsersListPage,
  validateSearch: (search) => usersSearchSchema.parse(search),
  staticData: {
    title: "Usuários",
  },
});

function UserActionsCell({ row }: { row: SmartUserDto }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const tenant = user?.tenant || "default-domain";

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isPasswordResetConfirmOpen, setIsPasswordResetConfirmOpen] = useState(false);
  const [isLogsModalOpen, setIsLogsModalOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const enableMutation = useEnableUser({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["listUser"],
        });
        setIsConfirmOpen(false);
      },
    }
  });

  const disableMutation = useDisableUser({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["listUser"],
        });
        setIsConfirmOpen(false);
      },
    }
  });

  const deleteMutation = useDeleteUser({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["listUser"],
        });
        setIsDeleteConfirmOpen(false);
      },
    }
  });

  const createPasswordLinkMutation = useCreatePasswordLink({
    mutation: {
      onSuccess: () => {
        setIsPasswordResetConfirmOpen(false);
        setSnackbar({
          open: true,
          message: t("admin.users.passwordResetSuccess", { email: row.email }),
          severity: "success",
        });
      },
      onError: () => {
        setSnackbar({
          open: true,
          message: t("admin.users.passwordResetError"),
          severity: "error",
        });
      },
    },
  });

  const handleToggleStatus = () => {
    if (row.enabled) {
      disableMutation.mutate({
        userId: row.id!,
        tenant,
      });
    } else {
      enableMutation.mutate({
        userId: row.id!,
        tenant,
      });
    }
  };

  const handleDelete = () => {
    deleteMutation.mutate({
      userId: row.id!,
      tenant,
    });
  };

  const handlePasswordReset = () => {
    if (!row.id) return;

    createPasswordLinkMutation.mutate({
      userId: String(row.id),
    });
  };

  const isStatusLoading = enableMutation.isPending || disableMutation.isPending;
  const isDeleteLoading = deleteMutation.isPending;
  const isPasswordResetLoading = createPasswordLinkMutation.isPending;

  return (
    <>
      <IconButton onClick={handleClick} size="small">
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem
          onClick={() => {
            handleClose();
            navigate({ to: `/admin/users/${row.id}` });
          }}
        >
          <ListItemIcon>
            <ViewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("common.view")}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleClose();
            navigate({ to: `/admin/users/${row.id}/edit` });
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("common.edit")}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleClose();
            setIsConfirmOpen(true);
          }}
        >
          <ListItemIcon>
            {row.enabled ? (
              <BlockIcon fontSize="small" color="error" />
            ) : (
              <CheckCircleIcon fontSize="small" color="success" />
            )}
          </ListItemIcon>
          <ListItemText>{row.enabled ? t("common.deactivate") : t("common.activate")}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleClose();
            setIsLogsModalOpen(true);
          }}
        >
          <ListItemIcon>
            <HistoryIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Log</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleClose();
            setIsPasswordResetConfirmOpen(true);
          }}
        >
          <ListItemIcon>
            <PasswordIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("admin.users.resendPassword")}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleClose();
            setIsDeleteConfirmOpen(true);
          }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>{t("common.delete")}</ListItemText>
        </MenuItem>
      </Menu>

      <ConfirmModal
        open={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={handleToggleStatus}
        title={row.enabled ? t("admin.users.deactivateTitle") : t("admin.users.activateTitle")}
        description={row.enabled ? t("admin.users.deactivateConfirmation", { name: row.name }) : t("admin.users.activateConfirmation", { name: row.name })}
        confirmText={row.enabled ? t("common.deactivate") : t("common.activate")}
        confirmColor={row.enabled ? "error" : "primary"}
        isLoading={isStatusLoading}
      />

      <ConfirmModal
        open={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        title={t("admin.users.deleteTitle")}
        description={t("admin.users.deleteConfirmation", { name: row.name })}
        confirmText={t("common.delete")}
        confirmColor="error"
        isLoading={isDeleteLoading}
      />

      <ConfirmModal
        open={isPasswordResetConfirmOpen}
        onClose={() => setIsPasswordResetConfirmOpen(false)}
        onConfirm={handlePasswordReset}
        title={t("admin.users.resendPasswordTitle")}
        description={t("admin.users.resendPasswordConfirmation", {
          name: row.name,
          email: row.email,
        })}
        confirmText={t("admin.users.resendPassword")}
        confirmColor="primary"
        isLoading={isPasswordResetLoading}
      />

      <LogsModal
        open={isLogsModalOpen}
        onClose={() => setIsLogsModalOpen(false)}
        entityId={row.id || ""}
        entityName={row.name || ""}
        tenant={tenant}
        entityType="users"
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

function UsersListPage() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { user } = useAuth();
  const navigate = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();
  const tenant = user?.tenant || "default-domain";
  const [isExporting, setIsExporting] = useState(false);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [exportStatusFilter, setExportStatusFilter] =
    useState<ExportStatusFilter>("all");
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const {
    data: usersData,
    isLoading,
    error,
  } = useQuery({
    ...getListUserQueryOptions(tenant, {
      page: search.page - 1,
      size: search.size,
      q: search.q,
      cpf: search.cpf,
      matriculaInstitucional: search.matricula,
      stockingAreaIds: search.stockingAreaId
        ? [parseInt(search.stockingAreaId)]
        : undefined,
      jobId: search.jobId ? parseInt(search.jobId) : undefined,
      accessProfileIds: search.accessProfileId
        ? [parseInt(search.accessProfileId)]
        : undefined,
    } as any),
  });

  const data = usersData as any;

  const handleFilter = (filters: UserFiltersData) => {
    const cleanFilters = Object.fromEntries(
      Object.entries(filters).filter(([_, v]) => v !== "" && v !== undefined),
    );

    navigate({
      search: (prev) => {
        const newSearch = { ...prev };
        const filterKeys = [
          "q",
          "cpf",
          "matricula",
          "stockingAreaId",
          "jobId",
          "accessProfileId",
        ];
        filterKeys.forEach((key) => {
          delete (newSearch as any)[key];
        });

        return {
          ...newSearch,
          ...cleanFilters,
          page: 1,
        };
      },
    });
  };

  const handleClearFilters = () => {
    navigate({
      search: (prev) => ({
        page: 1,
        size: prev.size,
      }),
    });
  };

  const handlePageChange = (_: unknown, newPage: number) => {
    navigate({
      search: (prev) => ({
        ...prev,
        page: newPage + 1,
      }),
    });
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    navigate({
      search: (prev) => ({
        ...prev,
        size: parseInt(event.target.value, 10),
        page: 1,
      }),
    });
  };

  const handleExport = async (statusFilter: ExportStatusFilter) => {
    try {
      setIsExporting(true);

      const params = new URLSearchParams();
      appendQueryParam(params, "isPageable", "false");
      appendQueryParam(
        params,
        "enabled",
        statusFilter === "all" ? undefined : statusFilter === "active",
      );
      appendQueryParam(params, "q", search.q);
      appendQueryParam(params, "cpf", search.cpf);
      appendQueryParam(params, "matriculaInstitucional", search.matricula);
      appendQueryParam(
        params,
        "stockingAreaIds",
        search.stockingAreaId ? parseInt(search.stockingAreaId) : undefined,
      );
      appendQueryParam(
        params,
        "jobId",
        search.jobId ? parseInt(search.jobId) : undefined,
      );
      appendQueryParam(
        params,
        "accessProfileIds",
        search.accessProfileId ? parseInt(search.accessProfileId) : undefined,
      );

      const queryString = params.toString();
      const exportResponse = await customInstanceBpm<any>(
        `/v1/${tenant}/users${queryString ? `?${queryString}` : ""}`,
        {
          method: "GET",
        },
      );

      const csv = buildUsersCsv(getUsersFromExportResponse(exportResponse), t);
      const fileBlob = new Blob([`\uFEFF${csv}`], {
        type: "text/csv;charset=utf-8",
      });
      downloadBlob(fileBlob, `usuarios-${new Date().toISOString().slice(0, 10)}.csv`);
      setIsExportDialogOpen(false);
    } catch (error) {
      console.error("Erro ao exportar usuários", error);
      setSnackbar({
        open: true,
        message: t("admin.users.exportError"),
        severity: "error",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const columnHelper = createColumnHelper<SmartUserDto>();

  const columns = useMemo(
    () => [
      columnHelper.accessor("id", {
        header: "ID",
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("name", {
        header: t("common.name"),
        cell: (info) => (
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {info.getValue()}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {info.row.original.email}
            </Typography>
          </Box>
        ),
      }),
      columnHelper.accessor("stockingAreas", {
        header: t("admin.users.sector"),
        cell: (info) => {
          const sectors = info.getValue();
          return sectors && sectors.length > 0 ? sectors[0]?.name : "-";
        },
      }),
      columnHelper.accessor("job", {
        header: t("admin.users.role"),
        cell: (info) => info.getValue()?.name || "-",
      }),
      columnHelper.accessor("enabled", {
        header: t("admin.common.situation"),
        cell: (info) => (
          <Chip
            label={info.getValue() ? t("common.active") : t("common.inactive")}
            color={info.getValue() ? "success" : "default"}
            size="small"
            variant="outlined"
          />
        ),
      }),
      columnHelper.display({
        id: "actions",
        header: t("common.actions"),
        cell: (info) => <UserActionsCell row={info.row.original} />,
        meta: { align: "right", isSticky: true },
      }),
    ],
    [columnHelper],
  );

  const table = useReactTable({
    data: data?.content || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: data?.totalPages || -1,
  });

  const handleCloseExportDialog = () => {
    if (!isExporting) {
      setIsExportDialogOpen(false);
    }
  };

  return (
    <ListLayout
      title="Usuários"
      subtitle={t("admin.users.subtitle")}
      isLoading={isLoading}
      error={error}
      isEmpty={!data?.content?.length}
      actions={
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title={isExporting ? t("common.exporting") : t("common.export")}>
            <span>
              <IconButton
                onClick={() => setIsExportDialogOpen(true)}
                disabled={isExporting}
                sx={{
                  bgcolor: "background.paper",
                  color: "primary.main",
                  border: "1px solid",
                  borderColor: "primary.main",
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                {isExporting ? (
                  <CircularProgress size={24} />
                ) : (
                  <FileDownloadIcon />
                )}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t("common.create")}>
            <IconButton
              onClick={() => navigate({ to: "/admin/users/create" })}
              sx={{
                bgcolor: "primary.main",
                color: "primary.contrastText",
                "&:hover": { bgcolor: "primary.dark" },
                boxShadow: 2,
              }}
            >
              <AddIcon />
            </IconButton>
          </Tooltip>
        </Box>
      }
      filters={
        <UserFilters
          onFilter={handleFilter}
          onClear={handleClearFilters}
          isLoading={isLoading}
          search={search}
        />
      }
    >
      {isMobile ? (
        <UserCards users={data?.content || []} />
      ) : (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{ border: "1px solid", borderColor: "divider" }}
        >
          <Table size="small">
            <TableHead sx={{ bgcolor: "background.default" }}>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const meta: any = header.column.columnDef.meta || {};
                    return (
                      <TableCell
                        key={header.id}
                        sx={{
                          fontWeight: 600,
                          ...(meta.isSticky && {
                            position: "sticky",
                            right: 0,
                            bgcolor: "background.default",
                            zIndex: 1,
                            boxShadow: "-2px 0 4px rgba(0,0,0,0.02)",
                          }),
                        }}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableHead>
            <TableBody>
              {table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  sx={{
                    bgcolor: "background.paper",
                    "&:hover": { bgcolor: "grey.50" },
                  }}
                >
                  {row.getVisibleCells().map((cell) => {
                    const meta: any = cell.column.columnDef.meta || {};
                    return (
                      <TableCell
                        key={cell.id}
                        sx={{
                          ...(meta.isSticky && {
                            position: "sticky",
                            right: 0,
                            bgcolor: "inherit",
                            zIndex: 1,
                            boxShadow: "-2px 0 4px rgba(0,0,0,0.02)",
                          }),
                        }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <TablePagination
        component="div"
        count={data?.totalElements || 0}
        page={(search.page || 1) - 1}
        onPageChange={handlePageChange}
        rowsPerPage={search.size || 10}
        onRowsPerPageChange={handleRowsPerPageChange}
        labelRowsPerPage="Linhas por página"
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
        }
        rowsPerPageOptions={[5, 10, 25, 50]}
        sx={{
          mt: 2,
          borderTop: "1px solid",
        borderColor: "divider",
        }}
      />
      <Dialog
        open={isExportDialogOpen}
        onClose={handleCloseExportDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t("admin.users.exportTitle")}</DialogTitle>
        <DialogContent>
          <FormControl component="fieldset" sx={{ mt: 1 }}>
            <FormLabel component="legend">
              {t("admin.users.exportStatusFilter")}
            </FormLabel>
            <RadioGroup
              value={exportStatusFilter}
              onChange={(event) =>
                setExportStatusFilter(event.target.value as ExportStatusFilter)
              }
            >
              <FormControlLabel
                value="active"
                control={<Radio />}
                label={t("common.active")}
              />
              <FormControlLabel
                value="inactive"
                control={<Radio />}
                label={t("common.inactive")}
              />
              <FormControlLabel
                value="all"
                control={<Radio />}
                label={t("common.all")}
              />
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseExportDialog} disabled={isExporting}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={() => handleExport(exportStatusFilter)}
            disabled={isExporting}
            startIcon={isExporting ? <CircularProgress size={18} /> : undefined}
          >
            {isExporting ? t("common.exporting") : t("common.export")}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </ListLayout>
  );
}
